import React from "react";
import { Row, Col, Button, Typography } from "antd";
import { Helmet } from "react-helmet-async";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield,
  Eye,
  Scale,
  Lock,
  AlertTriangle,
  RefreshCcw,
  Users,
  ChevronLeft,
  Heart,
  Sparkles,
} from "lucide-react";
import logoText from "@assets/images/logo/light-logo-text-nobg.png";
import { SpaceBackground } from "@components";

const { Title, Paragraph } = Typography;

const Ethics = () => {
  const history = useHistory();

  const principles = [
    {
      icon: Eye,
      title: "Transparency",
      description:
        "We maintain complete transparency about our AI systems' capabilities, limitations, and potential biases. Our commitment to openness extends to regular updates about our technological advancements and their implications for users.",
    },
    {
      icon: Scale,
      title: "Fairness & Inclusion",
      description:
        "We are dedicated to developing AI systems that promote equality and fairness. Our technology is designed to be accessible to all, actively working to eliminate biases and ensure equitable outcomes across all user groups.",
    },
    {
      icon: Shield,
      title: "Safety & Security",
      description:
        "Our AI systems undergo rigorous testing and continuous monitoring to ensure they meet the highest safety standards. We implement advanced security measures to protect our users and their data from potential threats.",
    },
    {
      icon: Lock,
      title: "Privacy Protection",
      description:
        "We employ state-of-the-art encryption and data protection measures to safeguard user privacy. Our transparent data practices ensure users maintain complete control over their personal information.",
    },
    {
      icon: AlertTriangle,
      title: "Responsible Innovation",
      description:
        "We strictly prohibit the use of our technology for harmful or unethical purposes. Our development process includes ethical considerations at every stage, ensuring our innovations benefit society.",
    },
    {
      icon: RefreshCcw,
      title: "Continuous Evolution",
      description:
        "We regularly update our ethical guidelines to reflect emerging best practices and technological advancements. Our commitment to improvement includes active engagement with ethics experts and stakeholders.",
    },
    {
      icon: Users,
      title: "Collaborative Ethics",
      description:
        "We carefully select partners who share our ethical standards and commitment to responsible AI development. Our ecosystem is built on shared values and mutual accountability.",
    },
  ];

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>AI Ethics & Responsibility | Copilotly</title>
        <meta
          name="description"
          content="Discover Copilotly's commitment to ethical AI development and responsible innovation. Learn about our principles and practices that ensure AI benefits humanity."
        />
        <meta
          name="keywords"
          content="AI ethics, responsible AI, ethical technology, AI principles, Copilotly ethics"
        />
        <link rel="canonical" href="https://app.copilotly.com/ethics" />
      </Helmet>

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "#0a0612",
          overflowY: "auto",
        }}
      >
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none" }}>
          <SpaceBackground />
        </div>
        <div
          style={{
            padding: "40px 20px 80px 20px",
            position: "relative",
            minHeight: "100%",
            zIndex: 1,
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              onClick={() => history.goBack()}
              style={{
                position: "fixed",
                top: 20,
                left: 20,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                background: "rgba(167, 139, 250, 0.15)",
                border: "1px solid rgba(167, 139, 250, 0.3)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                zIndex: 1000,
                color: "#ffffff",
                fontWeight: 500,
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                padding: "8px 16px",
                height: "auto",
              }}
            >
              <ChevronLeft size={18} style={{ color: "#a78bfa" }} />
              <span>Back</span>
            </Button>
          </motion.div>

          <Row justify="center">
            <Col xs={24} sm={24} md={20} lg={16} xl={14}>
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                style={{
                  textAlign: "center",
                  marginBottom: "40px",
                  marginTop: "60px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <img
                  src={logoText}
                  alt="Copilotly"
                  style={{
                    width: "200px",
                    marginBottom: "32px",
                    display: "block",
                  }}
                />

                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "rgba(167, 139, 250, 0.15)",
                    borderRadius: "20px",
                    padding: "8px 16px",
                    marginBottom: "20px",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    border: "1px solid rgba(167, 139, 250, 0.2)",
                  }}
                >
                  <Heart size={16} color="#a78bfa" />
                  <span style={{ color: "#c4b5fd", fontSize: "13px", fontWeight: 500 }}>
                    AI for Good
                  </span>
                </div>

                <h1
                  style={{
                    color: "#fff",
                    fontSize: "clamp(32px, 5vw, 48px)",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    margin: "0 0 16px 0",
                    letterSpacing: "-0.5px",
                  }}
                >
                  Our Commitment to{" "}
                  <span
                    style={{
                      background: "linear-gradient(135deg, #a78bfa 0%, #c4b5fd 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Ethical AI
                  </span>
                </h1>

                <p
                  style={{
                    color: "rgba(255,255,255,0.5)",
                    fontSize: "14px",
                    margin: 0,
                  }}
                >
                  Last updated: December 2024
                </p>
              </motion.div>

              {/* Intro Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                style={{
                  background: "rgba(167, 139, 250, 0.08)",
                  borderRadius: "20px",
                  padding: "28px",
                  marginBottom: "32px",
                  border: "1px solid rgba(167, 139, 250, 0.15)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "12px",
                      background: "rgba(167, 139, 250, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Sparkles size={20} color="#a78bfa" />
                  </div>
                  <span style={{ color: "#fff", fontSize: "18px", fontWeight: 600 }}>
                    Our Ethical Framework
                  </span>
                </div>
                <p
                  style={{
                    fontSize: "15px",
                    color: "rgba(255,255,255,0.7)",
                    margin: 0,
                    lineHeight: 1.7,
                  }}
                >
                  At Copilotly, we believe in harnessing the power of AI to create a better future for all. Our ethical framework ensures that our technology serves humanity while maintaining the highest standards of responsibility and integrity.
                </p>
              </motion.div>

              {/* Principles */}
              {principles.map((principle, index) => {
                const Icon = principle.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.15 + index * 0.05 }}
                    style={{
                      background: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "16px",
                      padding: "24px",
                      marginBottom: "16px",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                      backdropFilter: "blur(12px)",
                      WebkitBackdropFilter: "blur(12px)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "12px",
                          background: "rgba(167, 139, 250, 0.15)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon size={22} color="#a78bfa" />
                      </div>
                      <h3
                        style={{
                          margin: 0,
                          color: "#ffffff",
                          fontSize: "18px",
                          fontWeight: 600,
                        }}
                      >
                        {principle.title}
                      </h3>
                    </div>
                    <p
                      style={{
                        color: "rgba(255,255,255,0.65)",
                        fontSize: "15px",
                        lineHeight: 1.8,
                        margin: 0,
                        paddingLeft: "58px",
                      }}
                    >
                      {principle.description}
                    </p>
                  </motion.div>
                );
              })}

              {/* Commitment Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.55 }}
                style={{
                  background: "linear-gradient(135deg, rgba(167, 139, 250, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%)",
                  borderRadius: "20px",
                  padding: "32px",
                  marginTop: "32px",
                  marginBottom: "32px",
                  textAlign: "center",
                  border: "1px solid rgba(167, 139, 250, 0.2)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                }}
              >
                <h4
                  style={{
                    color: "#ffffff",
                    marginBottom: "16px",
                    fontSize: "20px",
                    fontWeight: 600,
                  }}
                >
                  Our Ongoing Commitment
                </h4>
                <p
                  style={{
                    color: "rgba(255,255,255,0.65)",
                    fontSize: "15px",
                    lineHeight: 1.7,
                    maxWidth: "600px",
                    margin: "0 auto 20px auto",
                  }}
                >
                  We recognize that ethical AI development is an evolving journey. Through continuous engagement with experts, regular policy reviews, and active participation in global AI ethics discussions, we ensure our practices remain at the forefront of responsible innovation.
                </p>
                <a
                  href="mailto:ethics@copilotly.com"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "rgba(167, 139, 250, 0.2)",
                    border: "1px solid rgba(167, 139, 250, 0.4)",
                    borderRadius: "12px",
                    padding: "12px 24px",
                    color: "#c4b5fd",
                    fontWeight: 500,
                    textDecoration: "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  ethics@copilotly.com
                </a>
              </motion.div>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default Ethics;
